import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, FileCheck } from "lucide-react";
import { PRODUCTS, localizeProducts } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const products = localizeProducts(lang);
  const activeSlug = searchParams.get("tab") || PRODUCTS[0].slug;
  const product = products.find((p) => p.slug === activeSlug) || products[0];

  return (
    <main data-testid="products-page" className="pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("products.kicker")}
          </p>
          <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t("products.titleA")} <span className="gold-text">{t("products.titleGold")}</span>
          </h1>
          <p className="mt-5 max-w-2xl leading-relaxed text-slate-400">{t("products.intro")}</p>
        </Reveal>

        {/* TABS */}
        <Reveal delay={0.1}>
          <div
            data-testid="product-tabs"
            className="mt-12 flex flex-wrap gap-2 border-b border-white/10 pb-px"
          >
            {products.map((p) => (
              <button
                key={p.slug}
                data-testid={`product-tab-${p.slug}`}
                onClick={() => setSearchParams({ tab: p.slug })}
                className={`relative px-5 py-4 font-display text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 sm:text-[13px] ${
                  product.slug === p.slug
                    ? "text-[#9db4ff]"
                    : "text-slate-400 hover:text-slate-100"
                }`}
              >
                {p.name}
                {product.slug === p.slug && (
                  <motion.span
                    layoutId="tab-underline"
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6]"
                  />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        {/* SPEC CARD */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${product.slug}-${lang}`}
            data-testid="product-spec-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid overflow-hidden border border-[#4a6cf7]/25 bg-[#0d223f] lg:grid-cols-5"
          >
            <div className="group relative lg:col-span-2">
              <img
                src={product.image}
                alt={product.name}
                className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d223f]/90 via-transparent to-[#0a192f]/30" />
              <div className="absolute bottom-5 left-5">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                  {product.short}
                </p>
                <h2 className="font-display mt-1 text-2xl font-bold text-white sm:text-3xl">
                  {product.name}
                </h2>
              </div>
            </div>

            <div className="p-8 lg:col-span-3 lg:p-12">
              <p className="leading-relaxed text-slate-300">{product.description}</p>

              <div className="mt-8 divide-y divide-white/5 border-y border-white/10">
                {product.specs.map((s) => (
                  <div
                    key={s.label}
                    data-testid={`spec-row-${s.label.toLowerCase().replace(/[^a-zà-ÿ]+/g, "-")}`}
                    className="grid grid-cols-3 gap-4 py-3.5"
                  >
                    <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#9db4ff]/90">
                      {s.label}
                    </span>
                    <span className="col-span-2 text-sm text-slate-200">{s.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-start gap-3 border border-[#4a6cf7]/20 bg-[#0a192f]/60 p-5">
                <FileCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#9db4ff]" />
                <p className="text-sm leading-relaxed text-slate-300">
                  <span className="font-semibold text-white">{t("products.applications")}</span>
                  {product.usage}
                </p>
              </div>

              <Link
                to={`/contact?commodity=${encodeURIComponent(product.commodity)}`}
                data-testid="product-rfq-button"
                className="group mt-8 inline-flex items-center gap-3 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                {t("products.rfq")} — {product.name}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </main>
  );
};

export default Products;
